// src/business/pages/BusinessAddProduct.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "../../utils/apparifySwal";

import Panel from "../components/Panel";
import { Button } from "../../components/ui";
import { businessSwalBase } from "../utils/businessSwal";
import { businessProductsAPI } from "../services/businessProductsService";

import StepBasicInfo from "../components/product/steps/StepBasicInfoCatalog";
import StepOptions from "../components/product/steps/StepOptions";
import StepVariants from "../components/product/steps/StepVariants";
import StepInventory from "../components/product/steps/StepInventory";
import StepMedia from "../components/product/steps/StepMediaAssets";
import Step3DAssetsViewer from "../components/product/steps/Step3DAssetsViewer";
import StepPublish from "../components/product/steps/StepPublish";
import WizardHeader from "../components/product/WizardHeader";

const STEPS = [
  { key: "basic", label: "Basic Info", description: "Name and organize the product in your catalog." },
  { key: "options", label: "Options", description: "Set the choices customers can make, such as size or color." },
  { key: "variants", label: "Variants", description: "Review the combinations, SKUs, and prices for each variant." },
  { key: "inventory", label: "Inventory", description: "Check stock visibility and choose when low-stock warnings appear." },
  { key: "media", label: "Media", description: "Add product images and assign media to the right variants." },
  { key: "threeD", label: "3D Assets & Viewer", description: "Configure optional 3D assets and the product viewer." },
  { key: "publish", label: "Review", description: "Check your listing before saving a draft or listing it for sale." },
];

const STEP_KEYS = STEPS.map((s) => String(s.key || "").toLowerCase());

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function stepIndexFromParam(stepParam) {
  const key = String(stepParam || "").toLowerCase().trim();
  const idx = STEP_KEYS.indexOf(key);
  return idx >= 0 ? idx : 0;
}

function statusFromErrMessage(msg) {
  const m = String(msg || "");
  const match = m.match(/Request failed:\s*(\d{3})/);
  return match ? Number(match[1]) : null;
}

function makeOptionId(seed = "opt") {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${seed}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

/**
 * Normalize payload for backend readiness:
 * - Trim strings
 * - Ensure numeric fields are numbers when possible
 * - Keep "empty" as "" when user hasn't provided value
 */
function normalizePayload(state) {
  const basic = {
    name: String(state.basic?.name || "").trim(),
    category: String(state.basic?.category || "").trim(),
    tags: Array.isArray(state.basic?.tags)
      ? [...new Set(state.basic.tags.map((tagId) => String(tagId || "").trim()).filter(Boolean))]
      : [],
    description: String(state.basic?.description || "").trim(),
    skuBase: String(state.basic?.skuBase || "").trim(),
  };

  const options = (state.options || []).map((o) => ({
    name: String(o?.name || "").trim(),
    values: (o?.values || [])
      .map((v) => String(v || "").trim())
      .filter(Boolean),
  }));

  const variants = (state.variants || []).map((v) => {
    // Price stays as a dollar string/number — backend converts to cents
    // Keep as-is but ensure it's a valid finite number
    const priceRaw = v?.price;
    let price;
    if (priceRaw === "" || priceRaw === null || typeof priceRaw === "undefined") {
      price = "";
    } else {
      const priceNum = Number(priceRaw);
      price = Number.isFinite(priceNum) ? priceNum : "";
    }

    return {
      optionValues: v?.optionValues || {},
      sku: String(v?.sku || "").trim(),
      imageUrl: v?.imageUrl ?? null,
      isActive: v?.isActive !== false,
      price,
    };
  });

  const inv = state.inventory || {};
  const lowStockRaw = inv.lowStockThreshold;
  let lowStockVal = "";
  if (lowStockRaw !== "" && lowStockRaw != null) {
    const n = Number(lowStockRaw);
    lowStockVal = Number.isFinite(n) && n >= 0 ? Math.floor(n) : "";
  }

  const inventory = {
    lowStockThreshold: lowStockVal,
  };

  const media = {
    productImages: Array.isArray(state.media?.productImages)
      ? state.media.productImages
      : [],
  };

  return { basic, options, variants, inventory, media };
}

export default function BusinessAddProduct() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initial step from URL
  const initialFromUrl = stepIndexFromParam(searchParams.get("step"));

  // Current step
  const [stepIndex, setStepIndex] = useState(initialFromUrl);
  const [showBasicRequiredErrors, setShowBasicRequiredErrors] = useState(false);

  // Furthest step ever reached (visited)
  const [maxVisitedIndex, setMaxVisitedIndex] = useState(initialFromUrl);

  const [state, setState] = useState({
    draftId: null,
    basic: { name: "", category: "", tags: [], description: "", skuBase: "" },
    options: [
      { id: makeOptionId("size"), name: "Size", values: [] },
      { id: makeOptionId("color"), name: "Color", values: [] },
    ],
    variants: [],
    inventory: {
      lowStockThreshold: "",
    },
    media: { productImages: [] },
    threeD: { variants: [] },
  });

  const swalBase = useMemo(
    () => ({
      ...businessSwalBase,
    }),
    []
  );

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveWarnings, setSaveWarnings] = useState([]);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const dirtyRef = useRef(false);
  const creatingDraftRef = useRef(false);

  const payload = useMemo(() => normalizePayload(state), [state]);

  // Helper to update URL step
  const setUrlStep = useCallback(
    (idx, opts = {}) => {
      const key = STEPS[idx]?.key || STEPS[0].key;
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("step", key);
      setSearchParams(nextParams, { replace: !!opts.replace });
    },
    [searchParams, setSearchParams]
  );

  /**
   * Core navigation rule:
   * - You can navigate to any step <= maxVisitedIndex (visited/back)
   * - You cannot jump ahead beyond maxVisitedIndex (even via URL)
   */
  const goToStep = useCallback(
    (idx, opts = {}) => {
      const desired = clamp(idx, 0, STEPS.length - 1);

      // Block jumping ahead beyond visited
      if (desired > maxVisitedIndex) return;

      setStepIndex(desired);
      setUrlStep(desired, opts);
    },
    [maxVisitedIndex, setUrlStep]
  );

  // Sync with URL changes (back/forward or manual edits)
  useEffect(() => {
    const urlIdx = stepIndexFromParam(searchParams.get("step"));

    // If URL tries to jump ahead beyond visited, snap back (replace)
    if (urlIdx > maxVisitedIndex) {
      goToStep(stepIndex, { replace: true });
      return;
    }

    // Allow moving back/within visited via URL
    if (urlIdx !== stepIndex) {
      setStepIndex(urlIdx);
    }
  }, [searchParams, maxVisitedIndex, goToStep, stepIndex]);

  // -- Lazy draft creation -------------------------------------------------
  // No database row is created until the user actually edits something.
  // The draft is created on the first auto-save or when the user explicitly
  // clicks Save Draft / List for Sale on the Review step.
  const ensureDraft = useCallback(async () => {
    if (state.draftId) return state.draftId;

    // Another call is already in flight - wait for it
    if (creatingDraftRef.current) {
      return new Promise((resolve, reject) => {
        const check = setInterval(() => {
          if (!creatingDraftRef.current) {
            clearInterval(check);
            resolve(null); // caller retries next cycle
          }
        }, 100);
        setTimeout(() => {
          clearInterval(check);
          reject(new Error("Draft creation timed out"));
        }, 10000);
      });
    }

    creatingDraftRef.current = true;

    try {
      const res = await businessProductsAPI.createDraft({ status: "DRAFT" });
      const id = res?.id || res?.draftId || res?.data?.id;
      if (!id) throw new Error("Unable to create draft. Check backend/API base URL.");

      setState((p) => ({ ...p, draftId: id }));
      setSaveError("");
      return id;
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Unable to create draft.";
      setSaveError(msg);

      const code = statusFromErrMessage(msg);
      if (code === 401) {
        await Swal.fire({ ...swalBase, icon: "error", title: "Login required", html: "Please sign in again to continue.", confirmButtonText: "OK" });
      } else if (code === 403) {
        await Swal.fire({ ...swalBase, icon: "error", title: "Access denied", html: "You don't have permission to create products.", confirmButtonText: "OK" });
      } else {
        await Swal.fire({ ...swalBase, icon: "error", title: "Draft creation failed", html: msg, confirmButtonText: "OK" });
      }
      return null;
    } finally {
      creatingDraftRef.current = false;
    }
  }, [state.draftId, swalBase]);

  // Ensure URL always has a step param on mount
  useEffect(() => {
    const existing = searchParams.get("step");
    if (!existing) setUrlStep(stepIndex, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mark dirty on user edits
  const setWizardState = useCallback((updater) => {
    setState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      dirtyRef.current = true;
      return next;
    });
  }, []);

  const saveNow = useCallback(async () => {
    setSaving(true);
    setSaveError("");
    try {
      const draftId = state.draftId || (await ensureDraft());
      if (!draftId) return false;
      const res = await businessProductsAPI.updateDraft(draftId, payload);
      dirtyRef.current = false;

      const warnings = res?.data?.warnings || res?.warnings || [];
      setSaveWarnings(Array.isArray(warnings) ? warnings : []);
      setLastSavedAt(new Date().toISOString());
      return true;
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Save failed. Check your backend.";
      setSaveWarnings([]);
      setSaveError(msg);
      return false;
    } finally {
      setSaving(false);
    }
  }, [ensureDraft, payload, state.draftId]);

  const confirmStepLeaveIfDirty = useCallback(async () => {
    if (!dirtyRef.current) return true;

    const result = await Swal.fire({
      ...swalBase,
      icon: "warning",
      title: "You have unsaved changes",
      html:
        '<p class="mb-1">Leaving now will lose any edits you\'ve made.</p>' +
        '<p class="text-xs text-neutral-500 dark:text-neutral-400">Save your changes first, or discard them to continue.</p>',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Save Changes",
      denyButtonText: "Discard",
      cancelButtonText: "Stay Here",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      return await saveNow();
    }
    if (result.isDenied) {
      dirtyRef.current = false;
      return true;
    }
    return false;
  }, [saveNow, swalBase]);

  const goNext = useCallback(() => {
    if (stepIndex === 0) {
      setShowBasicRequiredErrors(true);
    }
    if (stepIndex === 0 && (!payload.basic.name || !payload.basic.category)) {
      window.requestAnimationFrame(() => {
        if (!payload.basic.name) {
          document.getElementById("add-product-name")?.focus();
        } else {
          document.querySelector('button[aria-label="Product category"]')?.focus();
        }
      });
      return;
    }

    const next = clamp(stepIndex + 1, 0, STEPS.length - 1);
    setMaxVisitedIndex((m) => Math.max(m, next));
    setStepIndex(next);
    setUrlStep(next, { replace: false });

    window.requestAnimationFrame(() => {
      const content = document.querySelector(".business-content");
      if (content instanceof HTMLElement) {
        content.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    });
  }, [payload.basic.category, payload.basic.name, setUrlStep, stepIndex]);

  const goBack = useCallback(async () => {
    const canLeave = await confirmStepLeaveIfDirty();
    if (!canLeave) return;
    goToStep(stepIndex - 1);
  }, [confirmStepLeaveIfDirty, goToStep, stepIndex]);

  // -- Auto-save (debounced) - lazily creates draft on first save ----------
  useEffect(() => {
    if (!dirtyRef.current) return;

    const t = setTimeout(async () => {
      if (!dirtyRef.current) return;

      setSaving(true);
      setSaveError("");

      try {
        // Lazily create draft if it doesn't exist yet
        const draftId = state.draftId || (await ensureDraft());
        if (!draftId) {
          setSaving(false);
          return;
        }

        const res = await businessProductsAPI.updateDraft(draftId, payload);
        dirtyRef.current = false;

        const warnings = res?.data?.warnings || res?.warnings || [];
        setSaveWarnings(Array.isArray(warnings) ? warnings : []);
        setLastSavedAt(new Date().toISOString());
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Auto-save failed. Check your backend.";
        setSaveWarnings([]);
        setSaveError(msg);
      } finally {
        setSaving(false);
      }
    }, 650);

    return () => clearTimeout(t);
  }, [state.draftId, payload, ensureDraft]);

  // Prevent accidental browser close/refresh when dirty
  useEffect(() => {
    const handler = (e) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // BrowserRouter-safe guard for sidebar/top navigation links.
  useEffect(() => {
    const onDocumentClick = async (e) => {
      if (!dirtyRef.current) return;
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const anchor = e.target?.closest?.("a[href]");
      if (!anchor) return;
      if (anchor.target === "_blank" || anchor.hasAttribute("download")) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;

      const nextUrl = new URL(href, window.location.origin);
      if (nextUrl.origin !== window.location.origin) return;

      const current = `${window.location.pathname}${window.location.search}`;
      const next = `${nextUrl.pathname}${nextUrl.search}`;
      if (next === current) return;

      e.preventDefault();
      const canLeave = await confirmStepLeaveIfDirty();
      if (!canLeave) return;
      navigate(`${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    };

    document.addEventListener("click", onDocumentClick, true);
    return () => document.removeEventListener("click", onDocumentClick, true);
  }, [confirmStepLeaveIfDirty, navigate]);

  const Step = STEPS[stepIndex]?.key;
  const isFinalStep = stepIndex === STEPS.length - 1;

  // Step-level validation for WizardHeader badges
  const stepStates = useMemo(() => {
    const p = payload;
    return STEPS.map((s, idx) => {
      let issues = [];
      switch (s.key) {
        case "basic":
          // Quiet while the user is still filling Basic Info for the first time; once they try Next, or the step is
          // behind them (a resumed draft opened on a later step), it is judged on what it holds.
          if (showBasicRequiredErrors || idx !== stepIndex) {
            if (!p.basic.name) issues.push("Product name is required.");
            if (!p.basic.category) issues.push("Category is required.");
          }
          break;
        case "options": {
          const opts = p.options.filter((o) => o.name && o.values.length);
          if (!opts.length) issues.push("Add at least one option.");
          break;
        }
        case "variants":
          if (!p.variants.length) issues.push("Generate variants first.");
          break;
        case "media":
          if (!p.media.productImages.length) issues.push("Add at least 1 image.");
          break;
        case "threeD":
          issues = [];
          break;
        default:
          break;
      }
      const ok = issues.length === 0;
      // Current step: "current" if ok, "blocked" if has issues
      if (idx === stepIndex) return ok ? { status: "current" } : { status: "blocked", hint: issues[0] };
      // Visited steps: data-driven evaluation
      if (idx <= maxVisitedIndex) return ok ? { status: "done" } : { status: "blocked", hint: issues[0] };
      // Unvisited future steps: "todo"
      return { status: "todo" };
    });
  }, [maxVisitedIndex, payload, showBasicRequiredErrors, stepIndex]);

  // Header step click = go to visited step only
  const onStepClick = useCallback(
    (idx) => {
      goToStep(idx);
    },
    [goToStep]
  );

  return (
    <div className="space-y-4">
      <Panel
        title="Add Product"
        subtitle="Work through each step to build your listing. Review it before making it available for sale."
      >
        <WizardHeader
          steps={STEPS}
          stepIndex={stepIndex}
          stepStates={stepStates}
          saving={saving}
          error={saveError}
          warnings={saveWarnings}
          lastSavedAt={lastSavedAt}
          unsavedLabel="Not saved yet"
          onStepClick={onStepClick}
          maxClickableIndex={maxVisitedIndex}
          contained
        />

        <div className="mt-5 space-y-5">
          {Step === "basic" && (
            <StepBasicInfo
              state={state}
              setState={setWizardState}
              showRequiredErrors={showBasicRequiredErrors}
            />
          )}
          {Step === "options" && <StepOptions state={state} setState={setWizardState} />}
          {Step === "variants" && (
            <StepVariants
              state={state}
              setState={setWizardState}
              saveWarnings={saveWarnings}
            />
          )}
          {Step === "inventory" && <StepInventory state={state} setState={setWizardState} />}
          {Step === "media" && <StepMedia state={state} setState={setWizardState} />}
          {Step === "threeD" && (
            <Step3DAssetsViewer
              state={state}
              setState={setWizardState}
              ensureDraft={ensureDraft}
            />
          )}

          {Step === "publish" && (
            <StepPublish
              state={state}
              payload={payload}
              ensureDraft={ensureDraft}
            />
          )}

          <div
            className={
              isFinalStep
                ? "flex flex-col gap-2 rounded-xl border border-zinc-200 bg-zinc-50 p-3 sm:flex-row sm:items-center sm:justify-between dark:border-zinc-800 dark:bg-[#121214]"
                : "sticky bottom-0 z-20 flex flex-col gap-2 rounded-xl border border-zinc-200 bg-white/95 p-3 shadow-[0_-6px_20px_rgba(15,23,42,0.08)] backdrop-blur sm:flex-row sm:items-center sm:justify-between dark:border-zinc-800 dark:bg-[#121214]/95"
            }
          >
            <Button onClick={goBack} disabled={stepIndex === 0} className="w-full sm:w-auto">
              Back
            </Button>

            {stepIndex < STEPS.length - 1 && (
              <Button variant="primary" onClick={goNext} className="w-full sm:w-auto">
                Next: {STEPS[stepIndex + 1].label}
              </Button>
            )}
          </div>
        </div>
      </Panel>
    </div>
  );
}
